import { Component, inject, OnInit, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { RestService } from '../services/rest.service';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatCardModule } from '@angular/material/card';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatExpansionModule } from '@angular/material/expansion';
import { CommonModule } from '@angular/common';

import { BiomarkerCardComponent } from './biomarker-card/biomarker-card.component';
import { MatDialog } from '@angular/material/dialog';
import { SymptomsDetailInfoComponent } from '../symptoms-detail-info/symptoms-detail-info.component';
import { symptomsData } from '../symptoms-detail-info/data';

import { loaderComponent } from '../shared/components/loader/loader.component';
import { ConversionTrackingComponent } from './conversion-tracking/conversion-tracking.component';
import { SessionService } from '../services/session.service';
import { SkinFilterViewerComponent } from '../shared/components/skin-filter-viewer/skin-filter-viewer.component';
import { SkinRadarChartComponent } from './skin-radar-chart/skin-radar-chart.component';
import { BrandService } from '../../services/brand-config.service';
import { PerfectCorpResults } from '@server/entities/scan/perfectCorpResults';
import { TranslationService } from '../services/translation.service';
import { TranslatePipe } from '../pipes/translate.pipe';
import { ErrorReportingService } from '../services/error-reporting.service';

@Component({
  selector: 'app-full-results',
  standalone: true,
  imports: [
    CommonModule,
    MatToolbarModule,
    MatCardModule,
    MatProgressBarModule,
    MatButtonModule,
    MatIconModule,
    MatExpansionModule,
    BiomarkerCardComponent,
    loaderComponent,
    ConversionTrackingComponent,
    SkinFilterViewerComponent,
    SkinRadarChartComponent,
    TranslatePipe,
  ],
  templateUrl: './full-results.component.html',
  styleUrl: './full-results.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class FullResultsComponent implements OnInit {
  scanId: string;
  score = 85; // This should come from your backend
  showLoader = true;
  showSymtomsPage = false;
  showDoctorSection = false;
  symptomsInfo = [];
  symptom: string[];
  data: any;
  error: string;
  loading = true;
  brandName: string;
  perfectCorpResults: PerfectCorpResults;
  dialog = inject(MatDialog);

  result: any;
  skinAnalysisData: PerfectCorpResults = null;
  maskImages: { [key: string]: string } = {};
  private reporter = inject(ErrorReportingService);

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    public sessionService: SessionService,
    private restService: RestService,
    private brandService: BrandService,
    public translationService: TranslationService
  ) {
    this.scanId = this.route.snapshot.params['scanId'];
    this.brandName = this.brandService.config.brandName;
  }

  async ngOnInit() {
    this.reporter.logEvent('results.page.load');
    try {
      const response: any = await this.restService.request({
        path: `scan/${this.scanId}/full-results`,
        method: 'GET',
        throwError: true,
      });
      this.data = response.data;

      this.perfectCorpResults = this.data.scanResults;
      this.skinAnalysisData = this.data.scanResults;
      this.maskImages = this.data.maskImages || {};

      this.loading = false;
      if (this.data.userInfo.images) {
        this.base64Front = this.data.userInfo.images.front;
        this.showLoader = false;
      }
      if (this.data?.score?.analysis?.length > 0) {
        this.data?.score?.analysis?.forEach((analysis) => {
          if (analysis?.type == 'eye_analysis') {
            let totalEyeParameters = 0;
            analysis?.parameters?.forEach((eyeparameter) => {
              totalEyeParameters += eyeparameter?.detected_marks || 0;
            });
            analysis['parametersSum'] = totalEyeParameters;
          }
          if (analysis?.type == 'skin_analysis') {
            let totalSkinParameters = 0;
            analysis?.parameters?.forEach((skinparameter) => {
              if (skinparameter?.category == 'acne') {
                if (skinparameter?.detected_types) {
                  skinparameter?.detected_types.forEach((element) => {
                    totalSkinParameters += element?.detected_marks;
                  });
                }
              } else if (skinparameter?.category == 'skin_sensitivity') {
                totalSkinParameters += skinparameter?.sensitivity_level;
              } else if (skinparameter?.category == 'melasma') {
                totalSkinParameters += skinparameter?.detected_marks;
              } else if (skinparameter?.category == 'dry/oily_skin') {
                totalSkinParameters += skinparameter?.detected_blackheads;
              } else if (skinparameter?.category == 'pigmentation') {
                totalSkinParameters += skinparameter?.melanin_score;
              } else if (skinparameter?.category == 'wrinkles') {
                totalSkinParameters += skinparameter?.detected_wrinkles;
              }
            });
            analysis['parametersSum'] = totalSkinParameters;
          }
        });
      }
      this.result = this.data;
    } catch (error: any) {
      console.error('Failed to fetch full results:', error);
      this.reporter.report(
        `full-results page failed status=${error?.status} code=${error?.error?.code || ''}`,
        'results',
      );
      this.loading = false;

      if (error.error?.code) {
        switch (error.error.code) {
          case 400:
            if (
              error.error.message === 'Scan is missing required image data' ||
              error.error.message === 'Scan is missing AI analysis results'
            ) {
              this.error = this.translationService.translate(
                'fullResults.errorMissingImageData'
              );
              console.log('Scan is missing required image data');
              this.router.navigate(['/app/test'], {
                queryParams: { scanId: this.scanId },
              });
            }

            if (error.error.message === 'Scan is missing user information') {
              this.error = this.translationService.translate(
                'fullResults.errorMissingUserInfo'
              );
              this.router.navigate(['/app/symptoms', this.scanId]);
            }

            break;
          case 402:
            await this.router.navigateByUrl(
              `/app/initial-results/${this.scanId}`
            );
            return;
          case 403:
          case 404:
            this.error =
              error.error.message ||
              this.translationService.translate('fullResults.errorLoading');
            break;
          default:
            this.error = this.translationService.translate(
              'fullResults.errorUnexpected'
            );
        }
      } else {
        this.error = this.translationService.translate(
          'fullResults.errorUnexpected'
        );
      }
    }

    if (!this.error) {
      try {
        const response: any = await this.restService.request({
          path: `scan/${this.scanId}/symptoms`,
          method: 'GET',
        });
        if (response.data) {
          this.symptom = response.data.symptoms;
          this.symptomsInfo = symptomsData.filter((item1) =>
            this.symptom.some((item2) => item1.slug === item2)
          );
          this.showLoader = false;
        }
      } catch (error: any) {
        console.error('Failed to fetch Symptoms:', error);
        this.reporter.report(error, 'results');
      }
    }
  }

  base64Front: string | null = null;

  showSymptomsInfo(slug: string) {
    this.dialog.open(SymptomsDetailInfoComponent, {
      data: {
        details: this.symptomsInfo.filter((x) => x.slug == slug),
        key: slug,
      },
    });
  }

  moveToSymptoms() {
    // this.router.navigate(['/app/full-results', this.scanId, "symptoms"]);
    this.showSymtomsPage = true;
    setTimeout(() => {
      window.scrollTo(0, 0); // Scroll the entire page to the top
    });
  }
  moveToDoctor() {
    this.showDoctorSection = true;
    setTimeout(() => {
      window.scrollTo(0, 0); // Scroll the entire page to the top
    });
  }
  back() {
    // this.location.back();
    this.showDoctorSection = false;
    this.showSymtomsPage = false;
  }
  getLevel(score) {
    if (score <= 50) {
      return this.translationService.translate('fullResults.levelLow');
    } else if (score > 50 && score <= 70) {
      return this.translationService.translate('fullResults.levelMedium');
    } else {
      return this.translationService.translate('fullResults.levelHigh');
    }
  }
  getFname(name) {
    return name.split(' ')[0];
  }

  hasSkinAnalysisData(): boolean {
    return (
      this.skinAnalysisData &&
      typeof this.skinAnalysisData === 'object' &&
      Object.keys(this.skinAnalysisData).length > 0
    );
  }

  navigateToDashboard() {
    this.router.navigate(['/app/user-account']);
  }
}
